import Head from 'next/head';
import { getImageProps } from 'next/image';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MetaTags,
  AssetsPrefetchLinks,
  FavIconLinks,
  TwitterTags,
  TwitterCard,
  OpenGraphTags,
} from '@eventbrite/webapp-public-content';

import { useTranscendConsent } from '@eventbrite/legacy-infra.clid-courier';

import { Body } from '@components/Body';
import { Header } from '@components/Header';
import { getBaseDeepLinkAppUrl, JSONLDTags, SEOTags } from '@components/SEOTags';
import { getSocialImageUrl, SOCIAL_IMAGE_HEIGHT, SOCIAL_IMAGE_WIDTH } from '@components/SEOTags/socialImageMetadata';
import { TagManager } from '@/components/TagManager/TagManager';
import { GenericErrorPage } from '@/components/_shared/ErrorPages';
import { BasicInfoProvider } from '@/components/_shared/contexts/BasicInfoContext';
import { GalleryContextType, GalleryProvider } from '@/components/_shared/contexts/GalleryContext';
import { ExperimentsProvider } from '@/components/_shared/contexts/ExperimentsContext';
import { PreviewProvider } from '@/components/_shared/contexts/PreviewContext';
import { EditorRefreshBannerContent } from '@/components/NotificationBanner/notificationBannerUseCases';
import { resolveListingCanonicalUrl } from '@/utils/seriesChildListingSeo';

import { useEventDescription, useEventTitle } from '@/hooks';
import type { ListingPageProps } from '@/pages/listingPageTypes';
import { getUserDataQueryOptions } from '@/hooks/useUserData/useUserData';
import {
  useAttributionTrackingEffect,
  useEditorContextRefresh,
  useEventViewTrackingEffect,
  useExperimentAssignmentMismatchEffect,
  usePagePropertiesTrackingEffect,
} from '@/components/EventListingPage/hooks';

const TWITTER_IOS_REFERRER = 'eiosurlxtcar';
const TWITTER_ANDROID_REFERRER = 'eandurlxtcar';

export type EventListingPageProps = ListingPageProps & {
  /** When set, renders <link rel="canonical" href={...} /> in Head (e.g. protected page points to gate URL). */
  canonicalHref?: string | null;
  /** OG/Twitter description. If not provided, uses basicInfo.isProtected ? '' : basicInfo.summary. */
  ogDescription?: string;
  /** When true, skips the client refresh query because context already came from the editor-only endpoint. */
  skipFreshEditorContextFetch?: boolean;
};

export function EventListingPage({
  context,
  locale,
  error,
  stage,
  googleMapsApiKey,
  ebHost,
  query,
  experiments = {},
  globalHeaderProps,
  canonicalHref,
  ogDescription,
  isPreview,
  skipFreshEditorContextFetch = false,
  initialMoreOrganizerEvents,
}: EventListingPageProps): JSX.Element {
  const [pageContext, setPageContext] = useState(context);
  const { isInitialized } = useTranscendConsent();

  const { data: userData } = useQuery(
    getUserDataQueryOptions(pageContext?.basicInfo?.id, pageContext?.basicInfo?.isOnline, !!pageContext),
  );

  const eventId = pageContext?.basicInfo?.id;
  const canRefreshEditorContext = !!eventId && !!userData?.canManageEvent && !skipFreshEditorContextFetch;
  const { data: editorContextData, isFetching: isRefreshingEditorContext } = useEditorContextRefresh({
    eventId,
    enabled: canRefreshEditorContext,
  });

  useEffect(() => {
    setPageContext(context);
  }, [context]);

  useEffect(() => {
    if (editorContextData?.basicInfo) {
      setPageContext(editorContextData);
    }
  }, [editorContextData]);

  useEventViewTrackingEffect({ pageContext, isPreview });
  usePagePropertiesTrackingEffect({ pageContext, userData });
  useAttributionTrackingEffect({ pageContext, isInitialized });
  useExperimentAssignmentMismatchEffect({ experiments });

  const eventDescription = useEventDescription(pageContext?.basicInfo, locale);
  const eventTitle = useEventTitle(pageContext?.basicInfo, locale);

  if (error) {
    return <GenericErrorPage error={error} stage={stage} globalHeaderProps={globalHeaderProps} />;
  }

  if (!pageContext) {
    return <GenericErrorPage stage={stage} globalHeaderProps={globalHeaderProps} />;
  }

  const { basicInfo, collections, gallery, tags, structuredContent, seo, presenterInfo } = pageContext;
  const agendaInfo = structuredContent?.widgets?.agenda;
  const primaryImageUrl = getSocialImageUrl(gallery?.images);

  const {
    props: { src },
  } = getImageProps({
    alt: '',
    width: SOCIAL_IMAGE_WIDTH,
    height: SOCIAL_IMAGE_HEIGHT,
    src: primaryImageUrl || '',
  });

  const description = ogDescription ?? (basicInfo.isProtected ? '' : basicInfo.summary);
  const listingCanonicalUrl = resolveListingCanonicalUrl(basicInfo) ?? basicInfo.url;

  return (
    <PreviewProvider isPreview={!!isPreview}>
      <Head>
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription} />
        {canonicalHref && <link rel="canonical" href={canonicalHref} />}
        <AssetsPrefetchLinks />
        <FavIconLinks stage={stage} />
        {primaryImageUrl && <meta property="og:image:width" content={SOCIAL_IMAGE_WIDTH.toString()} />}
        {primaryImageUrl && <meta property="og:image:height" content={SOCIAL_IMAGE_HEIGHT.toString()} />}
      </Head>
      <MetaTags showDefaultDescription={false} />
      <OpenGraphTags
        url={listingCanonicalUrl}
        title={basicInfo.name}
        description={description}
        imageUrl={src}
        type="events.event"
      />
      <TwitterTags
        description={description}
        title={basicInfo.name}
        imageUrl={src}
        iosAppUrl={getBaseDeepLinkAppUrl(basicInfo.id, query)(TWITTER_IOS_REFERRER)}
        androidAppUrl={getBaseDeepLinkAppUrl(basicInfo.id, query)(TWITTER_ANDROID_REFERRER)}
        twitterCard={TwitterCard.SummaryLargeImage}
      />
      <SEOTags ebHost={ebHost} basicInfo={basicInfo} query={query} seo={seo} locale={locale} isPreview={!!isPreview} />
      {!isPreview && (
        <JSONLDTags
          artistInfo={pageContext?.artistInfo}
          basicInfo={basicInfo}
          gallery={gallery}
          primaryImageUrl={primaryImageUrl}
          salesStatus={pageContext?.salesStatus}
          seo={seo}
          structuredContent={structuredContent}
          tags={tags}
        />
      )}

      <Header globalHeaderProps={globalHeaderProps} />
      <ExperimentsProvider experiments={experiments}>
        <GalleryProvider gallery={gallery ?? ({} as GalleryContextType)}>
          <BasicInfoProvider basicInfo={basicInfo}>
            <Body
              basicInfo={basicInfo}
              collections={collections}
              gallery={gallery}
              tags={tags}
              taxonomies={pageContext?.taxonomies}
              performers={pageContext?.performers}
              structuredContent={structuredContent}
              artistInfo={pageContext?.artistInfo}
              agendaInfo={agendaInfo}
              goodToKnow={pageContext?.goodToKnow}
              googleMapsApiKey={googleMapsApiKey || ''}
              presenterInfo={presenterInfo}
              salesStatus={pageContext?.salesStatus}
              salesEndedMessageData={pageContext?.salesEndedMessageData}
              stage={stage}
              notificationBannerOverride={isRefreshingEditorContext ? <EditorRefreshBannerContent /> : undefined}
              initialMoreOrganizerEvents={initialMoreOrganizerEvents}
            />
          </BasicInfoProvider>
        </GalleryProvider>
      </ExperimentsProvider>

      {!isPreview && <TagManager publicUserId="" stage={stage} />}
    </PreviewProvider>
  );
}
